import { calcularConcepto, calcularTotales, formatearMoneda, tienePrecioFijo } from "@uirtus/shared";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { Servicio } from "../lib/servicios";
import { ResumenTotales } from "./resumen-totales";
import { SelectorServicio } from "./selector-servicio";
import { Aviso, Boton, BotonIcono, Campo, Entrada, Seleccion } from "./ui";

export interface ConceptoLocal {
  /** Identidad local para React y para editar/quitar filas. No se manda a la API. */
  clave: string;
  servicioId?: string;
  claveProdServ: string;
  claveUnidad: string;
  unidad?: string;
  descripcion: string;
  cantidad: string;
  valorUnitario: string;
  descuento: string;
  objetoImp: string;
  tasaIva: string | null;
}

/**
 * Opciones de IVA atadas a su ObjetoImp correcto.
 *
 * "Exento" no es "02 con IVA en null": fiscalmente es "01, no objeto de
 * impuesto". Guardar el objeto de impuesto por separado de la tasa dejaría
 * la puerta abierta a una combinación inconsistente; aquí van de la mano.
 */
const OPCIONES_IVA = [
  { valor: "0.160000", texto: "16%", objetoImp: "02" },
  { valor: "0.080000", texto: "8% (frontera)", objetoImp: "02" },
  { valor: "0.000000", texto: "0% (tasa cero)", objetoImp: "02" },
  { valor: "", texto: "No objeto de impuesto", objetoImp: "01" },
];

export const nuevoConceptoLibre = (): ConceptoLocal => ({
  clave: crypto.randomUUID(),
  claveProdServ: "81111500",
  claveUnidad: "E48",
  descripcion: "",
  cantidad: "1",
  valorUnitario: "",
  descuento: "0",
  objetoImp: "02",
  tasaIva: "0.160000",
});

export const conceptoDesdeServicio = (servicio: Servicio): ConceptoLocal => ({
  clave: crypto.randomUUID(),
  servicioId: servicio.id,
  claveProdServ: servicio.claveProdServ,
  claveUnidad: servicio.claveUnidad,
  unidad: servicio.unidad ?? undefined,
  descripcion: servicio.descripcion,
  cantidad: "1",
  // Solo se prellena si el servicio tiene tarifa fija; si se cotiza por
  // proyecto, se deja vacío para que el precio no pase inadvertido.
  valorUnitario: tienePrecioFijo(servicio) && servicio.precioBase ? servicio.precioBase : "",
  descuento: "0",
  objetoImp: servicio.objetoImp,
  tasaIva: servicio.tasaIva,
});

const soloNumeroDecimal = (v: string): string => v.replace(/[^\d.]/g, "");

interface Props {
  value: ConceptoLocal[];
  onChange: (nuevo: ConceptoLocal[]) => void;
  moneda: string;
  error?: string;
}

export function EditorConceptos({ value, onChange, moneda, error }: Props) {
  const [selectorAbierto, setSelectorAbierto] = useState(false);

  const actualizar = (clave: string, cambios: Partial<ConceptoLocal>) =>
    onChange(value.map((c) => (c.clave === clave ? { ...c, ...cambios } : c)));

  const eliminar = (clave: string) => onChange(value.filter((c) => c.clave !== clave));

  const mover = (indice: number, paso: number) => {
    const destino = indice + paso;
    if (destino < 0 || destino >= value.length) return;
    const copia = value.slice();
    [copia[indice], copia[destino]] = [copia[destino]!, copia[indice]!];
    onChange(copia);
  };

  // Los importes se recalculan aquí con la misma función que usa el backend,
  // así que lo que se ve en pantalla es exactamente lo que la API va a guardar.
  const calculos = useMemo(
    () =>
      value.map((c) =>
        calcularConcepto(
          {
            cantidad: c.cantidad || "0",
            valorUnitario: c.valorUnitario || "0",
            descuento: c.descuento,
            objetoImp: c.objetoImp,
            tasaIva: c.tasaIva,
          },
          moneda,
        ),
      ),
    [value, moneda],
  );

  const totales = useMemo(() => calcularTotales(calculos, moneda), [calculos, moneda]);

  return (
    <div className="space-y-3">
      {error && <Aviso>{error}</Aviso>}

      {value.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700">
          Agrega al menos un servicio para calcular el total.
        </p>
      )}

      <div className="space-y-2">
        {value.map((c, i) => {
          const calc = calculos[i]!;
          const necesitaPrecio = !!c.servicioId && !c.valorUnitario;

          return (
            <div
              key={c.clave}
              className="rounded-lg border border-slate-200 p-3 dark:border-slate-800"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Entrada
                    value={c.descripcion}
                    onChange={(e) => actualizar(c.clave, { descripcion: e.target.value })}
                    placeholder="Descripción del concepto"
                    className="font-medium"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Entrada
                      value={c.claveProdServ}
                      onChange={(e) =>
                        actualizar(c.clave, {
                          claveProdServ: e.target.value.replace(/\D/g, "").slice(0, 8),
                        })
                      }
                      placeholder="ClaveProdServ"
                      inputMode="numeric"
                      className="w-36 font-mono text-xs"
                    />
                    <Entrada
                      value={c.claveUnidad}
                      onChange={(e) =>
                        actualizar(c.clave, { claveUnidad: e.target.value.toUpperCase() })
                      }
                      placeholder="Unidad SAT"
                      className="w-28 font-mono text-xs"
                    />
                    {c.servicioId && (
                      <span className="text-xs text-slate-400">del catálogo</span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 gap-0.5">
                  <BotonIcono etiqueta="Subir" disabled={i === 0} onClick={() => mover(i, -1)}>
                    <ArrowUp size={14} />
                  </BotonIcono>
                  <BotonIcono
                    etiqueta="Bajar"
                    disabled={i === value.length - 1}
                    onClick={() => mover(i, 1)}
                  >
                    <ArrowDown size={14} />
                  </BotonIcono>
                  <BotonIcono
                    etiqueta="Quitar concepto"
                    variante="peligro"
                    onClick={() => eliminar(c.clave)}
                  >
                    <Trash2 size={14} />
                  </BotonIcono>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                <Campo etiqueta="Cantidad">
                  <Entrada
                    value={c.cantidad}
                    onChange={(e) =>
                      actualizar(c.clave, { cantidad: soloNumeroDecimal(e.target.value) })
                    }
                    inputMode="decimal"
                    className="font-mono"
                  />
                </Campo>
                <Campo
                  etiqueta="Valor unitario"
                  error={necesitaPrecio ? "Captura el precio" : undefined}
                >
                  <Entrada
                    value={c.valorUnitario}
                    onChange={(e) =>
                      actualizar(c.clave, {
                        valorUnitario: soloNumeroDecimal(e.target.value),
                      })
                    }
                    error={necesitaPrecio}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="font-mono"
                  />
                </Campo>
                <Campo etiqueta="Descuento">
                  <Entrada
                    value={c.descuento}
                    onChange={(e) =>
                      actualizar(c.clave, { descuento: soloNumeroDecimal(e.target.value) })
                    }
                    inputMode="decimal"
                    placeholder="0.00"
                    className="font-mono"
                  />
                </Campo>
                <Campo etiqueta="IVA">
                  <Seleccion
                    value={c.tasaIva ?? ""}
                    onChange={(e) => {
                      const opcion =
                        OPCIONES_IVA.find((o) => o.valor === e.target.value) ??
                        OPCIONES_IVA[0]!;
                      actualizar(c.clave, {
                        tasaIva: opcion.valor || null,
                        objetoImp: opcion.objetoImp,
                      });
                    }}
                  >
                    {OPCIONES_IVA.map((o) => (
                      <option key={o.texto} value={o.valor}>
                        {o.texto}
                      </option>
                    ))}
                  </Seleccion>
                </Campo>
                <div className="flex flex-col justify-end text-right">
                  <span className="text-xs text-slate-500">Importe</span>
                  <span className="font-mono text-sm font-medium">
                    {formatearMoneda(calc.importe, moneda)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <Boton variante="secundario" type="button" onClick={() => setSelectorAbierto(true)}>
          Agregar servicio del catálogo
        </Boton>
        <Boton
          variante="secundario"
          type="button"
          onClick={() => onChange([...value, nuevoConceptoLibre()])}
        >
          Agregar concepto libre
        </Boton>
      </div>

      {value.length > 0 && (
        <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
          <ResumenTotales
            subtotal={totales.subTotal}
            descuento={totales.descuento}
            iva={totales.totalImpuestosTrasladados}
            retenciones={totales.totalImpuestosRetenidos}
            total={totales.total}
            moneda={moneda}
          />
        </div>
      )}

      <SelectorServicio
        abierto={selectorAbierto}
        alCerrar={() => setSelectorAbierto(false)}
        alSeleccionar={(s) => onChange([...value, conceptoDesdeServicio(s)])}
      />
    </div>
  );
}
